import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Briefcase, Users, Mail } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { JOBS, CAREERS_EMAIL } from "../data/site";
import { Reveal, Eyebrow } from "../components/Reveal";

const applyMailto = (job) => {
  const subject = encodeURIComponent(`Application: ${job.title}, ${job.location}`);
  const body = encodeURIComponent(
    `Hi Mobile Maintenance team,\n\nI'd like to apply for the ${job.title} position in ${job.location}.\n\nName:\nPhone:\nRelevant experience:\n\n(Attach resume if available)\n\nThanks,`
  );
  return `mailto:${CAREERS_EMAIL}?subject=${subject}&body=${body}`;
};

const TEAMS = ["All", ...Array.from(new Set(JOBS.map((j) => j.team)))];

export default function Careers() {
  const [filter, setFilter] = useState("All");
  const filtered = useMemo(
    () => (filter === "All" ? JOBS : JOBS.filter((j) => j.team === filter)),
    [filter]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Careers | Mobile Maintenance, Join the Field-First Fleet Team";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      "Now hiring across Texas: mobile mechanics, shop mechanics, PM leads, sales, admin, and more. Join Mobile Maintenance."
    );
  }, []);

  return (
    <>
      <Navbar />
      <main className="bg-mm-base pt-28 sm:pt-32" data-testid="careers-page">
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-mm-green/15">
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.06]"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(0,177,64,0.4) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,177,64,0.4) 1px, transparent 1px)",
              backgroundSize: "80px 80px",
            }}
          />
          <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 sm:py-28 relative">
            <div className="grid grid-cols-12 gap-8 items-end">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="col-span-12 lg:col-span-9"
              >
                <Eyebrow>Now Hiring · Texas</Eyebrow>
                <h1 className="mt-5 font-heading font-black uppercase tracking-tight text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.9]">
                  Build your career <br />
                  <span className="text-mm-green">in the field.</span>
                </h1>
                <p className="mt-8 max-w-2xl text-gray-300 text-lg leading-relaxed">
                  Mobile Maintenance runs on skilled people. If you take pride in your work,
                  care about doing it right, and want to be part of a team that keeps America's
                  fleets running. We want to hear from you.
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="col-span-12 lg:col-span-3 lg:text-right"
              >
                <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-mm-green mb-2">
                  Open Roles
                </div>
                <div className="font-heading font-black text-white text-6xl sm:text-7xl leading-none">
                  {JOBS.length}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="border-b border-mm-green/15">
          <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-8 flex flex-wrap gap-3">
            {TEAMS.map((t) => (
              <button
                key={t}
                onClick={() => setFilter(t)}
                data-testid={`careers-filter-${t.toLowerCase().replace(/\s+/g, "-")}`}
                className={`font-mono text-[11px] tracking-[0.2em] uppercase px-4 py-2 border transition-colors ${
                  filter === t
                    ? "border-mm-green bg-mm-green text-black"
                    : "border-mm-green/25 text-gray-400 hover:border-mm-green hover:text-mm-green"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </section>

        {/* Jobs list */}
        <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-16 sm:py-20">
          <div className="divide-y divide-mm-green/15 border-y border-mm-green/15">
            {filtered.map((job, i) => (
              <motion.article
                key={job.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.05 }}
                data-testid={`job-${job.slug}`}
                className="py-10 grid grid-cols-12 gap-6 items-start hover:bg-mm-surface/40 transition-colors px-2 sm:px-4"
              >
                {/* Left: title + meta */}
                <div className="col-span-12 lg:col-span-4">
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-mm-green mb-3">
                    {String(i + 1).padStart(2, "0")} · {job.team}
                  </div>
                  <h2 className="font-heading font-black uppercase text-white text-3xl sm:text-4xl leading-[0.95]">
                    {job.title}
                  </h2>
                  <div className="mt-4 flex flex-wrap items-center gap-4 text-gray-400 text-sm">
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin size={14} className="text-mm-green" /> {job.location}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Briefcase size={14} className="text-mm-green" /> {job.type}
                    </span>
                  </div>
                </div>

                {/* Middle: summary + details */}
                <div className="col-span-12 lg:col-span-6 space-y-6">
                  <p className="text-gray-300 leading-relaxed">{job.summary}</p>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-mm-green mb-3">
                        What You'll Do
                      </div>
                      <ul className="space-y-2">
                        {job.responsibilities.map((r) => (
                          <li key={r} className="text-gray-400 text-sm leading-snug flex gap-2">
                            <span className="text-mm-green mt-1.5 shrink-0 w-1 h-1 rounded-full bg-mm-green" />
                            <span>{r}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-mm-green mb-3">
                        What We're Looking For
                      </div>
                      <ul className="space-y-2">
                        {job.requirements.map((r) => (
                          <li key={r} className="text-gray-400 text-sm leading-snug flex gap-2">
                            <span className="text-mm-green mt-1.5 shrink-0 w-1 h-1 rounded-full bg-mm-green" />
                            <span>{r}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Right: Apply CTA */}
                <div className="col-span-12 lg:col-span-2 lg:text-right">
                  <a
                    href={applyMailto(job)}
                    data-testid={`apply-${job.slug}`}
                    className="inline-flex items-center gap-2 font-heading font-bold uppercase tracking-wider bg-mm-green text-black px-5 py-3 hover:bg-white transition-colors"
                  >
                    Apply <ArrowRight size={16} />
                  </a>
                </div>
              </motion.article>
            ))}
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="bg-mm-panel border-y border-mm-green/20">
          <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-16 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center gap-3 text-mm-green mb-4">
                <Users size={22} />
                <span className="font-mono text-[11px] tracking-[0.3em] uppercase">
                  Don't See Your Role?
                </span>
              </div>
              <h3 className="font-heading font-black uppercase text-white text-3xl sm:text-4xl leading-tight">
                We're always looking for <span className="text-mm-green">good people</span>.
              </h3>
              <p className="mt-4 text-gray-400 max-w-md">
                If you have field-service, heavy-equipment, or fleet experience and want to talk,
                send us your resume and let's have a conversation.
              </p>
            </div>
            <div className="md:text-right">
              <a
                href={`mailto:${CAREERS_EMAIL}?subject=${encodeURIComponent("General Application")}`}
                data-testid="general-application"
                className="inline-flex items-center gap-3 font-heading font-bold uppercase tracking-wider bg-mm-green text-black px-7 py-4 text-lg hover:bg-white transition-colors"
              >
                <Mail size={20} /> Email Us Your Resume
              </a>
              <div className="mt-3 font-mono text-xs uppercase tracking-widest text-gray-500">
                {CAREERS_EMAIL}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
